// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faVideo, 
    faPlus, 
    faLink,
    faList,
    faSync,
    faCheck,
    faClock,
    faTrash,
    faEdit,
    faEye,
    faUpload,
    faSpinner,
    faExclamationTriangle,
    faSearch,
    faClosedCaptioning
} from '@fortawesome/free-solid-svg-icons';
import { 
    getYouTubeVideos, 
    processYouTubeVideo, 
    deleteYouTubeVideo,
    approveYouTubeVideo,
    syncYouTubeChannel,
    getYouTubeProcessingStatus,
    getApiBaseUrl
} from '../services/api';
import { toast } from 'react-toastify';
import AddVideoModal from '../components/AddVideoModal';
import ProcessingStatusModal from '../components/ProcessingStatusModal';
import YouTubeProgressToast from '../components/YouTubeProgressToast';
import TranscriptViewer from '../components/TranscriptViewer';
import ChannelSyncProgress from '../components/ChannelSyncProgress';

interface YouTubeVideo {
    id: number;
    video_id: string;
    title: string;
    channel_name: string;
    duration: number;
    thumbnail_url: string;
    video_url: string;
    status: 'pending_transcription' | 'pending_review' | 'approved' | 'processing' | 'failed' | 'completed';
    actual_status?: string;
    created_at: string;
    processed_at?: string;
    error_message?: string;
    has_transcript?: boolean;
}

interface YouTubeVideosProps {
    clientId: string | null;
}

const YouTubeVideos: React.FC<YouTubeVideosProps> = ({ clientId }) => {
    const [videos, setVideos] = useState<YouTubeVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [selectedVideo, setSelectedVideo] = useState<YouTubeVideo | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showProcessingModal, setShowProcessingModal] = useState(false);
    const [showProgressToast, setShowProgressToast] = useState(false);
    const [processingStatus, setProcessingStatus] = useState<any>(null);
    const [sseConnection, setSseConnection] = useState<EventSource | null>(null);
    const [showChannelSync, setShowChannelSync] = useState(false);
    const [syncingChannelUrl, setSyncingChannelUrl] = useState<string>('');
    const [showTranscriptViewer, setShowTranscriptViewer] = useState(false);

    useEffect(() => {
        if (clientId) {
            fetchVideos();
            // Do initial check to see if we need SSE
            checkProcessingStatus();
        }
    }, [clientId]);

    const fetchVideos = async () => {
        if (!clientId) return;
        
        try {
            setLoading(true);
            const data = await getYouTubeVideos(clientId);
            setVideos(data.data?.items || []);
        } catch (error) {
            console.error('Error fetching videos:', error);
            toast.error('Failed to load YouTube videos');
        } finally {
            setLoading(false);
        }
    };

    const checkProcessingStatus = async () => {
        if (!clientId) return;
        
        try {
            const status = await getYouTubeProcessingStatus(clientId);
            setProcessingStatus(status);
            
            // Only set up SSE if actively processing
            if (status.is_processing && !sseConnection) {
                setupSSEConnection();
            }
        } catch (error) {
            console.error('Error checking processing status:', error);
        }
    };

    const setupSSEConnection = () => {
        if (!clientId || sseConnection) return;
        
        console.log('[YouTube] Setting up SSE connection for processing status');
        
        const eventSource = new EventSource(
            `${getApiBaseUrl()}/youtube/status/${clientId}/stream`,
            { withCredentials: true }
        );
        
        eventSource.onopen = () => {
            console.log('[YouTube] SSE connection established');
        };
        
        eventSource.onmessage = (event) => {
            try {
                const status = JSON.parse(event.data);
                setProcessingStatus(status);
                
                // Show progress toast instead of modal
                if (status.is_processing) {
                    setShowProgressToast(true);
                    // Auto-hide modal if it's open
                    if (showProcessingModal) {
                        setShowProcessingModal(false);
                    }
                }
                
                // Close connection and modal when done
                if (!status.is_processing) {
                    console.log('[YouTube] Processing complete, closing SSE');
                    eventSource.close();
                    setSseConnection(null);
                    fetchVideos(); // Refresh the list
                    // Toast will auto-hide after showing completion
                }
            } catch (error) {
                console.error('[YouTube] Error parsing SSE data:', error);
            }
        };
        
        eventSource.onerror = (error) => {
            console.error('[YouTube] SSE connection error:', error);
            eventSource.close();
            setSseConnection(null);
            
            // Don't auto-reconnect to prevent server overload
            // User can manually refresh or perform actions to reconnect
        };
        
        setSseConnection(eventSource);
    };

    // Clean up SSE on unmount or when clientId changes
    useEffect(() => {
        return () => {
            if (sseConnection) {
                console.log('[YouTube] Cleaning up SSE connection');
                sseConnection.close();
                setSseConnection(null);
            }
        };
    }, [sseConnection]);

    const handleAddVideo = async (url: string) => {
        if (!clientId) return;
        
        setLoading(true);
        try {
            // Always add videos in pending_review state (no auto-approve)
            const response = await processYouTubeVideo(clientId, url, true);
            
            if (response.success) {
                toast.success(
                    <div>
                        <p>{response.message || 'Video added successfully'}</p>
                        {response.title && <p className="text-sm mt-1">"{response.title}"</p>}
                        <p className="text-xs mt-2 opacity-80">Video added to review queue</p>
                    </div>,
                    { duration: 5000 }
                );
                setShowAddModal(false);
                await fetchVideos();
            } else {
                toast.error(response.message || 'Failed to add video');
            }
        } catch (error) {
            console.error('Error adding video:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to add video';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSyncChannel = async (channelUrl: string) => {
        if (!clientId) return;
        
        try {
            // Show channel sync progress immediately
            setSyncingChannelUrl(channelUrl);
            setShowChannelSync(true);
            setShowAddModal(false);
            
            // The actual sync happens via SSE in ChannelSyncProgress component
        } catch (error) {
            console.error('Error syncing channel:', error);
            toast.error('Failed to sync channel');
        }
    };

    const handleApproveVideo = async (videoId: number) => {
        if (!clientId) return;
        
        try {
            await approveYouTubeVideo(clientId, videoId);
            toast.success('Video approved and added to knowledge base');
            setShowReviewModal(false);
            
            // Video will now be processed, start SSE monitoring with toast
            setShowProgressToast(true);
            await checkProcessingStatus();
            fetchVideos();
        } catch (error) {
            console.error('Error approving video:', error);
            toast.error('Failed to approve video');
        }
    };

    const handleDeleteVideo = async (videoId: number) => {
        if (!clientId) return;
        
        if (!window.confirm('Are you sure you want to delete this video?')) {
            return;
        }
        
        try {
            await deleteYouTubeVideo(clientId, videoId);
            toast.success('Video deleted successfully');
            fetchVideos();
        } catch (error) {
            console.error('Error deleting video:', error);
            toast.error('Failed to delete video');
        }
    };

    const formatDuration = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <FontAwesomeIcon icon={faCheck} className="text-brand-500" />;
            case 'approved':
                return <FontAwesomeIcon icon={faCheck} className="text-brand-500" />;
            case 'pending_review':
                return <FontAwesomeIcon icon={faClock} className="text-yellow-500" />;
            case 'pending_transcription':
                return <FontAwesomeIcon icon={faClosedCaptioning} className="text-orange-500" />;
            case 'processing':
                return <FontAwesomeIcon icon={faSpinner} className="text-brand-500 animate-spin" />;
            case 'failed':
                return <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500" />;
            default:
                return null;
        }
    };

    const filteredVideos = videos.filter(video => {
        const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            video.channel_name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || video.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    if (!clientId) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Please select a client to view YouTube videos</p>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                            <FontAwesomeIcon icon={faVideo} className="text-red-500" />
                            YouTube Videos
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Manage YouTube video transcripts for your knowledge base
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 flex items-center gap-2"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Add Video
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <FontAwesomeIcon 
                                icon={faSearch} 
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type="text"
                                placeholder="Search videos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                            />
                        </div>
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                        <option value="all">All Status</option>
                        <option value="completed">In Knowledge Base</option>
                        <option value="pending_transcription">Pending Transcription</option>
                        <option value="pending_review">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="processing">Processing</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
            </div>

            {/* Videos Grid */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <FontAwesomeIcon icon={faSpinner} className="text-4xl text-brand-500 animate-spin" />
                </div>
            ) : filteredVideos.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                    <FontAwesomeIcon icon={faVideo} className="text-6xl text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg mb-4">
                        {searchTerm || filterStatus !== 'all' 
                            ? 'No videos found matching your criteria' 
                            : 'No YouTube videos added yet'}
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800"
                        >
                            Add Your First Video
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVideos.map((video) => (
                        <div key={video.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                            {/* Thumbnail */}
                            <div className="relative aspect-video bg-gray-100">
                                <img
                                    src={video.thumbnail_url}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    onError={(e) => {
                                        e.currentTarget.src = '/placeholder-video.png';
                                    }}
                                />
                                <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-sm">
                                    {formatDuration(video.duration)}
                                </div>
                                <div className="absolute top-2 right-2">
                                    {getStatusIcon(video.status)}
                                </div>
                                {/* Click overlay to view transcript */}
                                <div 
                                    className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all cursor-pointer flex items-center justify-center group"
                                    onClick={() => {
                                        setSelectedVideo(video);
                                        setShowTranscriptViewer(true);
                                    }}
                                >
                                    <div className="bg-white px-4 py-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                        <FontAwesomeIcon icon={faEye} className="mr-2" />
                                        View Transcript
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="p-4">
                                <h3 className="font-semibold text-gray-800 line-clamp-2 mb-2">
                                    {video.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    {video.channel_name}
                                </p>

                                {/* Status Badge */}
                                <div className="flex items-center justify-between mb-4">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                        ${video.status === 'completed' ? 'bg-brand-100 text-brand-800' : ''}
                                        ${video.status === 'approved' ? 'bg-brand-100 text-brand-700' : ''}
                                        ${video.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' : ''}
                                        ${video.status === 'pending_transcription' ? 'bg-orange-100 text-orange-800' : ''}
                                        ${video.status === 'processing' ? 'bg-brand-100 text-brand-700' : ''}
                                        ${video.status === 'failed' ? 'bg-red-100 text-red-800' : ''}
                                    `}>
                                        {video.status === 'completed' ? 'In Knowledge Base' : 
                                         video.status === 'pending_transcription' ? 'Pending Transcription' :
                                         video.status.replace('_', ' ').charAt(0).toUpperCase() + video.status.slice(1).replace('_', ' ')}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {new Date(video.created_at).toLocaleDateString()}
                                    </span>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2">
                                    {video.status === 'pending_transcription' && (
                                        <button
                                            onClick={() => {
                                                setSelectedVideo(video);
                                                setShowTranscriptViewer(true);
                                            }}
                                            className="flex-1 bg-orange-500 text-white px-3 py-1.5 rounded text-sm hover:bg-orange-600 flex items-center justify-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faClosedCaptioning} />
                                            Transcribe
                                        </button>
                                    )}
                                    {video.status === 'pending_review' && (
                                        <button
                                            onClick={() => {
                                                setSelectedVideo(video);
                                                setShowTranscriptViewer(true);
                                            }}
                                            className="flex-1 bg-brand-500 text-white px-3 py-1.5 rounded text-sm hover:bg-black flex items-center justify-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                            Review
                                        </button>
                                    )}
                                    <a
                                        href={video.video_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 bg-gray-100 text-gray-700 px-3 py-1.5 rounded text-sm hover:bg-gray-200 flex items-center justify-center gap-1"
                                    >
                                        <FontAwesomeIcon icon={faLink} />
                                        View
                                    </a>
                                    <button
                                        onClick={() => handleDeleteVideo(video.id)}
                                        className="bg-red-100 text-red-700 px-3 py-1.5 rounded text-sm hover:bg-red-200"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modals */}
            {showAddModal && (
                <AddVideoModal
                    onClose={() => setShowAddModal(false)}
                    onAddVideo={handleAddVideo}
                    onSyncChannel={handleSyncChannel}
                />
            )}

            {showTranscriptViewer && selectedVideo && (
                <TranscriptViewer
                    video={selectedVideo}
                    clientId={clientId}
                    onClose={() => {
                        setShowTranscriptViewer(false);
                        setSelectedVideo(null);
                    }}
                    onAddToKnowledgeBase={() => {
                        // Refresh the video list to show updated status
                        fetchVideos();
                    }}
                />
            )}

            {showProcessingModal && processingStatus && (
                <ProcessingStatusModal
                    status={processingStatus}
                    onClose={() => setShowProcessingModal(false)}
                />
            )}

            {/* Progress Toast */}
            {showProgressToast && (
                <YouTubeProgressToast
                    status={processingStatus}
                    onClose={() => {
                        setShowProgressToast(false);
                        if (sseConnection) {
                            sseConnection.close();
                            setSseConnection(null);
                        }
                    }}
                />
            )}


            {/* Channel Sync Progress */}
            {showChannelSync && syncingChannelUrl && (
                <ChannelSyncProgress
                    clientId={clientId}
                    channelUrl={syncingChannelUrl}
                    onClose={() => {
                        setShowChannelSync(false);
                        setSyncingChannelUrl('');
                    }}
                    onComplete={() => {
                        // Refresh videos list after sync
                        fetchVideos();
                    }}
                />
            )}
        </div>
    );
};

export default YouTubeVideos;